import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Radar, Users, Armchair, AlarmClock, ClipboardList, BusFront, Satellite, Search, Phone, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LiveMap from "@/components/LiveMap";
import StopTimeline, { sourceLabel } from "@/components/StopTimeline";
import ManifestDialog from "@/pages/admin/ManifestDialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtTime } from "@/lib/format";

function OccupancyMap({ data }) {
  const bySeat = {};
  (data.passengers || []).forEach((p) => { bySeat[p.seat] = p; });
  const tip = (p) => (p ? `Seat ${p.seat} · ${p.name}\n${p.phone || ""}\nKin: ${p.kin_name || "-"} ${p.kin_phone || ""}${p.boarded ? "\nBoarded" : ""}` : "");
  const SeatBox = ({ n }) => {
    const p = bySeat[n];
    return (
      <div title={tip(p)} data-testid={`occ-seat-${n}`}
        className={`grid h-8 w-8 cursor-default place-items-center rounded-t-lg rounded-b-sm border text-[10px] font-bold ${p ? (p.boarded ? "border-emerald-300 bg-emerald-100 text-emerald-800" : "border-[#0052FF]/40 bg-blue-100 text-blue-800") : "border-slate-200 bg-slate-50 text-slate-400"}`}>
        {n}
      </div>
    );
  };
  const plan = data.plan;
  return (
    <div data-testid="occupancy-map">
      {plan ? (
        <div className="inline-grid gap-1" style={{ gridTemplateColumns: `repeat(${plan.cols}, 32px)` }}>
          {plan.cells.flatMap((row, r) => row.map((c, i) => (c.t === "seat" ? <SeatBox key={`${r}-${i}`} n={c.n} /> : <div key={`${r}-${i}`} className="h-8 w-8" />)))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-1">{Array.from({ length: data.capacity || 0 }, (_, i) => <SeatBox key={i + 1} n={i + 1} />)}</div>
      )}
      <div className="mt-2 flex flex-wrap gap-3 text-[10px] font-semibold text-slate-500">
        <span className="flex items-center gap-1"><i className="h-3 w-3 rounded border border-[#0052FF]/40 bg-blue-100" />Booked</span>
        <span className="flex items-center gap-1"><i className="h-3 w-3 rounded border border-emerald-300 bg-emerald-100" />Boarded</span>
        <span className="flex items-center gap-1"><i className="h-3 w-3 rounded border border-slate-200 bg-slate-50" />Empty</span>
      </div>
    </div>
  );
}

function PassengerPanel({ data }) {
  if (!data) return <div className="grid h-24 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-[#0052FF]" /></div>;
  return (
    <div className="space-y-3" data-testid="passenger-panel">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600"><UserRound className="h-4 w-4 text-[#0052FF]" />Driver: <b>{data.driver_name || "-"}</b>{data.driver_phone ? <span className="flex items-center gap-1 text-slate-500"><Phone className="h-3 w-3" />{data.driver_phone}</span> : null}</div>
      <div><div className="label-xs mb-1.5">Seating — hover a seat for passenger details</div><OccupancyMap data={data} /></div>
      <div className="overflow-hidden rounded-xl border border-slate-200">
        <table className="w-full text-left text-[11px]">
          <thead><tr className="bg-slate-50 text-slate-500"><th className="p-1.5">Seat</th><th>Passenger</th><th>Phone</th><th>Next of kin</th></tr></thead>
          <tbody>
            {data.passengers.map((p) => (
              <tr key={p.seat} className="border-t border-slate-100" data-testid={`pax-row-${p.seat}`}>
                <td className="p-1.5 font-bold">{p.seat}</td>
                <td>{p.name}{p.boarded ? <span className="ml-1 text-emerald-600" title="Boarded">●</span> : ""}</td>
                <td>{p.phone}</td>
                <td>{p.kin_name ? <span>{p.kin_name}<br /><span className="text-slate-400">{p.kin_phone}</span></span> : "-"}</td>
              </tr>
            ))}
            {!data.passengers.length && <tr><td colSpan={4} className="p-3 text-center text-slate-500">No passengers.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BusPopup({ b, onManifest }) {
  return (
    <div className="space-y-2 font-sans" data-testid={`bus-popup-${b.trip_id}`}>
      <div><div className="font-display text-base font-extrabold text-[#182678]">{b.bus_number}</div><div className="text-xs text-slate-500">{b.route} · {b.bus_type}</div></div>
      <div className="grid grid-cols-2 gap-1.5 text-xs">
        <div className="rounded-lg bg-blue-50 p-2"><div className="text-slate-500">Seats filled</div><b className="text-sm" data-testid={`popup-seats-${b.trip_id}`}>{b.seats_filled} / {b.capacity}</b></div>
        <div className="rounded-lg bg-emerald-50 p-2"><div className="text-slate-500">Passengers</div><b className="text-sm" data-testid={`popup-pax-${b.trip_id}`}>{b.passengers} ({b.boarded} boarded)</b></div>
      </div>
      <div className="text-xs text-slate-600">{b.next_stop ? <>Next: <b>{b.next_stop.city}</b> ~{fmtTime(b.next_stop.eta)}</> : "Arrived at destination"}{b.delay_min > 5 && <b className="text-rose-600"> · {b.delay_min} min late</b>}</div>
      <div className="text-[10px] text-slate-400">{sourceLabel(b.position)}{b.tracker_id ? ` · tracker ${b.tracker_id}` : ""} · driver {b.driver_name || "-"}</div>
      <Button size="sm" className="h-8 w-full rounded-full bg-[#0052FF] text-xs hover:bg-[#0041CC]" onClick={() => onManifest(b)} data-testid={`popup-manifest-${b.trip_id}`}><ClipboardList className="h-3.5 w-3.5" />View manifest</Button>
    </div>
  );
}

const Kpi = ({ icon: I, label, value, testid }) => (
  <div className="card-soft flex items-center gap-3 p-4"><span className="grid h-10 w-10 place-items-center rounded-xl bg-blue-50 text-[#0052FF]"><I className="h-5 w-5" /></span><div><div className="label-xs">{label}</div><div className="font-display text-2xl font-extrabold" data-testid={testid}>{value}</div></div></div>
);

function BusList({ buses, selected, onPick }) {
  return (
    <div className="space-y-2" data-testid="control-bus-list">
      {buses.map((b) => (
        <button key={b.trip_id} onClick={() => onPick(b)} data-testid={`control-bus-${b.trip_id}`}
          className={`btn-press w-full rounded-2xl border p-3 text-left transition-colors ${selected === b.trip_id ? "border-[#0052FF] bg-blue-50" : "border-slate-200 bg-white hover:border-blue-300"}`}>
          <div className="flex items-center justify-between gap-2"><b className="font-mono text-sm">{b.bus_number}</b><span className={`chip ${b.delay_min > 10 ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>{b.delay_min > 10 ? `${b.delay_min} min late` : b.status}</span></div>
          <div className="text-sm font-semibold">{b.route}</div>
          <div className="text-xs text-slate-500">{fmtTime(b.departure)} · {b.seats_filled}/{b.capacity} seats · next {b.next_stop?.city || "-"}</div>
        </button>
      ))}
    </div>
  );
}

export default function ControlRoom() {
  const { user } = useAuth();
  const [buses, setBuses] = useState(null);
  const [sel, setSel] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [query, setQuery] = useState("");
  const [geometries, setGeometries] = useState({});
  const [paxData, setPaxData] = useState(null);
  const load = useCallback(() => api.get("/tracking/live").then((r) => setBuses(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  const tripKey = (buses || []).map((b) => b.trip_id).join();
  useEffect(() => {
    (buses || []).forEach((b) => {
      api.get("/tracking/route-geometry", { params: { trip_id: b.trip_id } })
        .then((r) => setGeometries((g) => ({ ...g, [b.trip_id]: r.data.coords })))
        .catch(() => {});
    });
  }, [tripKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setPaxData(null);
    if (!sel) return;
    api.get(`/tracking/trips/${sel}/passengers`).then((r) => setPaxData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [sel]);
  const cur = buses?.find((b) => b.trip_id === sel);
  const canMark = (s, i) => i > 0 && (user.role === "admin" || (user.role === "station_master" && user.station_id === s.station_id));
  const mark = async (s, undo) => {
    try { await api.post(`/admin/trips/${cur.trip_id}/arrive`, { station_id: s.station_id, undo }); toast.success(undo ? "Arrival removed" : `Arrival recorded at ${s.city}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!buses) return <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0052FF]" /></div>;
  const pax = buses.reduce((a, b) => a + b.passengers, 0);
  const q = query.trim().toLowerCase();
  const filtered = q ? buses.filter((b) => `${b.bus_number} ${b.route}`.toLowerCase().includes(q)) : buses;
  return (
    <div className="mx-auto max-w-[1500px] space-y-5 px-4 py-6 sm:px-6" data-testid="control-room">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="flex items-center gap-2 font-display text-3xl font-extrabold text-[#182678]"><Radar className="h-7 w-7 text-[#0052FF]" />Control room</h1><p className="text-sm text-slate-500">Every bus on the road right now. Click a bus for seats, passengers and its manifest.</p></div>
        <span className="chip bg-amber-50 text-amber-700" data-testid="control-source-note"><Satellite className="h-3.5 w-3.5" />Positions: live GPS when a tracker reports, otherwise demo simulation</span>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi icon={BusFront} label="Live buses" value={buses.length} testid="kpi-live-buses" />
        <Kpi icon={Users} label="Passengers" value={pax} testid="kpi-passengers" />
        <Kpi icon={Armchair} label="Seats filled now" value={buses.reduce((a, b) => a + b.seats_filled, 0)} testid="kpi-seats" />
        <Kpi icon={AlarmClock} label="Delayed (>10 min)" value={buses.filter((b) => b.delay_min > 10).length} testid="kpi-delayed" />
      </div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by bus number or route (e.g. GW-1188 or Accra Kumasi)" className="h-11 rounded-full pl-10" data-testid="map-search-input" />
      </div>
      <div className="grid gap-5 xl:grid-cols-[1fr_360px]">
        <LiveMap buses={filtered} selected={sel} onSelect={(b) => setSel(b.trip_id)} geometries={geometries} renderPopup={(b) => <BusPopup b={b} onManifest={(x) => setManifest({ id: x.trip_id })} />} className="h-[420px] xl:h-[640px]" />
        <div className="space-y-4 xl:max-h-[640px] xl:overflow-y-auto">
          {cur && (
            <div className="card-soft p-4" data-testid="control-bus-detail">
              <div className="mb-3 flex items-center justify-between"><b>{cur.bus_number} · {cur.route}</b><Button size="sm" variant="outline" className="h-8 rounded-full text-xs" onClick={() => setManifest({ id: cur.trip_id })} data-testid="detail-manifest-button">Manifest</Button></div>
              <StopTimeline timeline={cur.timeline} canMark={canMark} onMark={mark} />
              <div className="mt-3 border-t border-slate-100 pt-3"><PassengerPanel data={paxData} /></div>
            </div>
          )}
          {filtered.length ? <BusList buses={filtered} selected={sel} onPick={(b) => setSel(b.trip_id)} /> : <div className="card-soft p-8 text-center text-sm text-slate-500" data-testid="control-empty">{query ? "No buses match your search." : "No buses on the road right now."}</div>}
        </div>
      </div>
      <ManifestDialog trip={manifest} onClose={() => setManifest(null)} canRelease={false} />
    </div>
  );
}
